import toast from 'react-hot-toast';
import { Dispatch } from 'redux';
import { actions as actionsStore } from '../../context';
import { Services } from '../../types/services';

const FETCH_PRACTICE_REQUEST = 'FETCH_PRACTICE_REQUEST';
const SEARCH_PRACTICES_RESULT = 'SEARCH_PRACTICES_RESULT';
const SEARCH_PRACTICE_ERROR = 'SEARCH_PRACTICE_ERROR';
const GET_LATEST_RUNTIME_EXTRACTION = 'GET_LATEST_RUNTIME_EXTRACTION';
const GET_LATEST_RUNTIME_ATTACHMENT_SYNC = 'GET_LATEST_RUNTIME_ATTACHMENT_SYNC';
const GET_LATEST_RUNTIME_STAGING = 'GET_LATEST_RUNTIME_STAGING';
const GET_LATEST_RUNTIME_INTERMEDIATE = 'GET_LATEST_RUNTIME_INTERMEDIATE';
const GET_LATEST_RUNTIME_INSTANCE_REPORT = 'GET_LATEST_RUNTIME_INSTANCE_REPORT';
const GET_LATEST_RUNTIME_CONVERSIONPREP = 'GET_LATEST_RUNTIME_CONVERSIONPREP';
const GET_LATEST_RUNTIME_LEADS_GENERATION = 'GET_LATEST_RUNTIME_LEADS_GENERATION';
const PRACTICE_ERROR = 'PRACTICE_ERROR';
const PRACTICE_TO_MUTE = 'PRACTICE_TO_MUTE';
const USER_ASSIGNING_PRACTICE = 'USER_ASSIGNING_PRACTICE';
const SELECT_PRACTICE = 'SELECT_PRACTICE';
const SET_ACTIVE_TAB_PRACTICES = 'SET_ACTIVE_TAB_PRACTICES';
const SET_ASSIGNED_USER = 'SET_ASSIGNED_USER';
const SET_BOOKMARKED = 'SET_BOOKMARKED';
const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';
const SET_DASHBOARD_STATS = 'SET_DASHBOARD_STATS';
const SET_FILTER = 'SET_FILTER';
const SET_INCLUDE_TEST_PRACTICES = 'SET_INCLUDE_TEST_PRACTICES';
const SET_INCLUDE_DORMANT_PRACTICES = 'SET_INCLUDE_DORMANT_PRACTICES';
const SET_PRACTICE_ID = 'SET_PRACTICE_ID';
const SET_PRACTICE_NOTIFICATIONS = 'SET_PRACTICE_NOTIFICATIONS';
const SET_SEARCH_ASSIGNED_PRACTICES = 'SET_SEARCH_ASSIGNED_PRACTICES';
const SET_SEARCH_CUSTOM_IDS = 'SET_SEARCH_CUSTOM_IDS';
const SET_SEARCH_GROUPS = 'SET_SEARCH_GROUPS';
const SET_SEARCH_NOTES = 'SET_SEARCH_NOTES';
const SET_SEARCH_PARTNER_OFFERINGS = 'SET_SEARCH_PARTNER_OFFERINGS';
const SET_SEARCH_PRACTICE_PIMS = 'SET_SEARCH_PRACTICE_PIMS';
const SET_RELATION_STEP2 = 'SET_RELATION_STEP2';
const REMOVE_RELATION_STEP2 = 'REMOVE_RELATION_STEP2';
const UNSELECT_PRACTICE = 'UNSELECT_PRACTICE';
const RESET_RELATION_STEP2 = 'RESET_RELATION_STEP2';
const SET_SCHEDULE_MODAL_WORKING_PRACTICE = 'SET_SCHEDULE_MODAL_WORKING_PRACTICE';
const SET_RELATIONS_MODAL_WORKING_PRACTICE = 'SET_RELATIONS_MODAL_WORKING_PRACTICE';
const SET_CONFIG_MODAL_WORKING_PRACTICE = 'SET_CONFIG_MODAL_WORKING_PRACTICE';
const GET_PRACTICE = 'GET_PRACTICE';
const SET_PRACTICE_GROUPS = 'SET_PRACTICE_GROUPS';
const SET_PRACTICE_SHARDS = 'SET_PRACTICE_SHARDS';
const SET_PRACTICE_SHARDS_LOADING = 'SET_PRACTICE_SHARDS_LOADING';
const SET_PRACTICE_SHARDS_UNAVAILABLE = 'SET_PRACTICE_SHARDS_UNAVAILABLE';
const SET_PRACTICE_SHARDS_FORBIDDEN = 'SET_PRACTICE_SHARDS_FORBIDDEN';

/**
 * Shard lookups cross a gRPC hop to the shard service, and the shared axios instance has no
 * default timeout — without this the practice page would wait indefinitely on an unresponsive
 * shard service. Scoped to this request on purpose; the global default is left alone.
 */
const SHARD_REQUEST_TIMEOUT_MS = 10000;

interface Practice {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

interface PracticeMute {
  muteUntil: number;
  muteNote: string | null;
}

interface PracticeToMute {
  id: string;
  name: string;
  practiceMute: PracticeMute;
}

interface UserAssigningPractice {
  id: string;
  name: string;
  assignedAccountId: string;
  isAssignedToCurrentUser: boolean;
}

interface ScheduleModalPractice {
  nextSyncDate: string;
  id: string;
}

interface RelationStep2 {
  partnerOfferingId: string;
  [key: string]: unknown;
}

interface PracticeShard {
  shardType: string;
  label: string;
  name: string | null;
  region: string | null;
  targetName: string | null;
  isAssigned: boolean;
  isMigrationPending: boolean;
  isMigrationStatusUnknown: boolean;
}

interface PracticeShardsResponse {
  practiceId: string;
  instanceId: string | null;
  isUnavailable: boolean;
  shards: PracticeShard[];
}

interface PracticeShardsState {
  /** Tracked so the UI can ignore a response that arrives after the user switched practices. */
  practiceId: string;
  /**
   * 'unavailable' means the shard service could not be read. 'forbidden' means the API rejected the
   * caller — a different thing, and the UI hides the block rather than claiming an outage.
   */
  status: 'idle' | 'loading' | 'loaded' | 'unavailable' | 'forbidden';
  shards: PracticeShard[];
}

interface State {
  assignedFilterUser: string | null;
  bookmarked: boolean;
  currentPage: number;
  dashboardInfo: Record<string, unknown>;
  error: string;
  filter: string;
  includeTestPractices: boolean;
  includeDormantPractices: boolean;
  intermediateEntityCounts: unknown[];
  isPracticeLoading: boolean;
  onlyShowAssigned: boolean;
  practice: Practice;
  practiceGroups: unknown[];
  practiceLink: string;
  practiceNotifications: number;
  practiceToMute: PracticeToMute;
  userAssigningModalPractice: UserAssigningPractice;
  relationsStep2: RelationStep2[];
  latestRuntimeExtraction: string;
  latestRuntimeAttachmentSync: string;
  latestRuntimeStaging: string;
  latestRuntimeIntermediate: string;
  latestRuntimeInstanceReport: string;
  latestRuntimeLeadsGeneration: string;
  latestRuntimeConversionPrep: string;
  currentPracticeId: string;
  searchGroups: boolean;
  searchNotes: boolean;
  searchCustomIds: boolean;
  searchPartnerOfferings: boolean;
  searchPracticePims: boolean;
  selectedPracticeTab: number;
  selectedPracticeTabs: Practice[];
  practiceSheduleModalWorkingPractice: ScheduleModalPractice;
  practiceRelationsModalWorkingPractice: Record<string, unknown>;
  practiceConfigModalWorkingPracticeId: string;
  practiceShards: PracticeShardsState;
}

const initialState: State = {
  assignedFilterUser: null,
  bookmarked: false,
  currentPage: 1,
  dashboardInfo: {},
  error: '',
  filter: '',
  includeTestPractices: false,
  includeDormantPractices: false,
  intermediateEntityCounts: [],
  isPracticeLoading: true,
  onlyShowAssigned: false,
  practice: {},
  practiceGroups: [],
  practiceLink: 'all',
  practiceNotifications: 0,
  practiceToMute: {
    id: '',
    name: '',
    practiceMute: { muteUntil: 0, muteNote: null },
  },
  userAssigningModalPractice: {
    id: '',
    name: '',
    assignedAccountId: '',
    isAssignedToCurrentUser: false,
  },
  relationsStep2: [],
  latestRuntimeExtraction: '',
  latestRuntimeAttachmentSync: '',
  latestRuntimeStaging: '',
  latestRuntimeIntermediate: '',
  latestRuntimeInstanceReport: '',
  latestRuntimeLeadsGeneration: '',
  latestRuntimeConversionPrep: '',
  currentPracticeId: '',
  searchGroups: false,
  searchNotes: false,
  searchCustomIds: false,
  searchPartnerOfferings: false,
  searchPracticePims: false,
  selectedPracticeTab: 0,
  selectedPracticeTabs: [],
  practiceSheduleModalWorkingPractice: { nextSyncDate: '', id: '' },
  practiceRelationsModalWorkingPractice: {},
  practiceConfigModalWorkingPracticeId: '',
  practiceShards: { practiceId: '', status: 'idle', shards: [] },
};

interface Action {
  type: string;
  payload?: unknown;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case UNSELECT_PRACTICE: {
      const payload = action.payload as { practice?: Practice; tabId: number };
      if (!payload.practice) {
        return {
          ...state,
          selectedPracticeTabs: state.selectedPracticeTabs.filter(t => t !== undefined),
        };
      }
      const selectedPractices = state.selectedPracticeTabs.filter(
        tabs => tabs.id !== payload.practice?.id,
      );
      let selectedTabId = state.selectedPracticeTab;
      const tabId = payload.tabId === -1 ? state.selectedPracticeTab : payload.tabId;
      if (tabId < selectedTabId) {
        selectedTabId = selectedTabId - 1;
      } else if (tabId === selectedTabId) {
        if (tabId >= selectedPractices.length) selectedTabId = selectedTabId - 1;
      }
      return {
        ...state,
        selectedPracticeTabs: selectedPractices,
        selectedPracticeTab: selectedTabId,
      };
    }
    case SELECT_PRACTICE: {
      const practice = action.payload as Practice;
      let selectedTab = state.selectedPracticeTab;
      const alreadySelected = state.selectedPracticeTabs.filter((tabs, index) => {
        if (tabs?.id === practice.id) {
          selectedTab = index;
          return true;
        }
        return false;
      });
      if ((alreadySelected || []).length > 0) {
        if (selectedTab === state.selectedPracticeTab) return state;
        return { ...state, selectedPracticeTab: selectedTab };
      }
      const selectedPracticeTabs = [...state.selectedPracticeTabs, practice];
      selectedTab = selectedPracticeTabs.length - 1;
      return { ...state, selectedPracticeTabs, selectedPracticeTab: selectedTab };
    }
    case SET_BOOKMARKED:
      return { ...state, bookmarked: action.payload as boolean };
    case SET_ACTIVE_TAB_PRACTICES:
      return { ...state, practiceLink: action.payload as string };
    case FETCH_PRACTICE_REQUEST:
      return { ...state, isPracticeLoading: true };
    case GET_PRACTICE:
      return {
        ...state,
        practice: action.payload as Practice,
        isPracticeLoading: false,
        error: '',
      };
    case PRACTICE_ERROR:
      return { ...state, isPracticeLoading: false, error: action.payload as string };
    case SET_PRACTICE_SHARDS_LOADING:
      return {
        ...state,
        practiceShards: {
          practiceId: action.payload as string,
          status: 'loading',
          shards: [],
        },
      };
    case SET_PRACTICE_SHARDS: {
      const payload = action.payload as { practiceId: string; shards: PracticeShard[] };
      return {
        ...state,
        practiceShards: {
          practiceId: payload.practiceId,
          status: 'loaded',
          shards: payload.shards,
        },
      };
    }
    case SET_PRACTICE_SHARDS_UNAVAILABLE:
      return {
        ...state,
        practiceShards: {
          practiceId: action.payload as string,
          status: 'unavailable',
          shards: [],
        },
      };
    case SET_PRACTICE_SHARDS_FORBIDDEN:
      return {
        ...state,
        practiceShards: {
          practiceId: action.payload as string,
          status: 'forbidden',
          shards: [],
        },
      };
    case GET_LATEST_RUNTIME_EXTRACTION:
      return { ...state, latestRuntimeExtraction: action.payload as string };
    case GET_LATEST_RUNTIME_ATTACHMENT_SYNC:
      return { ...state, latestRuntimeAttachmentSync: action.payload as string };
    case GET_LATEST_RUNTIME_STAGING:
      return { ...state, latestRuntimeStaging: action.payload as string };
    case GET_LATEST_RUNTIME_INTERMEDIATE:
      return { ...state, latestRuntimeIntermediate: action.payload as string };
    case GET_LATEST_RUNTIME_INSTANCE_REPORT:
      return { ...state, latestRuntimeInstanceReport: action.payload as string };
    case GET_LATEST_RUNTIME_CONVERSIONPREP:
      return { ...state, latestRuntimeConversionPrep: action.payload as string };
    case GET_LATEST_RUNTIME_LEADS_GENERATION:
      return { ...state, latestRuntimeLeadsGeneration: action.payload as string };
    case SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload as number };
    case SET_FILTER:
      return { ...state, filter: action.payload as string };
    case PRACTICE_TO_MUTE:
      return { ...state, practiceToMute: action.payload as PracticeToMute };
    case USER_ASSIGNING_PRACTICE:
      return { ...state, userAssigningModalPractice: action.payload as UserAssigningPractice };
    case SET_SEARCH_NOTES:
      return { ...state, searchNotes: action.payload as boolean };
    case SET_PRACTICE_NOTIFICATIONS:
      return { ...state, practiceNotifications: action.payload as number };
    case SET_SEARCH_ASSIGNED_PRACTICES:
      return { ...state, onlyShowAssigned: action.payload as boolean };
    case SET_PRACTICE_GROUPS:
      return { ...state, practiceGroups: action.payload as unknown[] };
    case SET_SEARCH_GROUPS:
      return { ...state, searchGroups: action.payload as boolean };
    case SET_DASHBOARD_STATS:
      return { ...state, dashboardInfo: action.payload as Record<string, unknown> };
    case SET_INCLUDE_TEST_PRACTICES:
      return { ...state, includeTestPractices: action.payload as boolean };
    case SET_INCLUDE_DORMANT_PRACTICES:
      return { ...state, includeDormantPractices: action.payload as boolean };
    case SET_ASSIGNED_USER:
      return { ...state, assignedFilterUser: action.payload as string | null };
    case SET_SEARCH_CUSTOM_IDS:
      return { ...state, searchCustomIds: action.payload as boolean };
    case SET_SEARCH_PARTNER_OFFERINGS:
      return { ...state, searchPartnerOfferings: action.payload as boolean };
    case SET_SEARCH_PRACTICE_PIMS:
      return { ...state, searchPracticePims: action.payload as boolean };
    case SET_PRACTICE_ID:
      return { ...state, currentPracticeId: action.payload as string };
    case SET_SCHEDULE_MODAL_WORKING_PRACTICE:
      return {
        ...state,
        practiceSheduleModalWorkingPractice: action.payload as ScheduleModalPractice,
      };
    case SET_RELATIONS_MODAL_WORKING_PRACTICE:
      return {
        ...state,
        practiceRelationsModalWorkingPractice: action.payload as Record<string, unknown>,
      };
    case SET_CONFIG_MODAL_WORKING_PRACTICE:
      return { ...state, practiceConfigModalWorkingPracticeId: action.payload as string };
    case SET_RELATION_STEP2: {
      const offerings = action.payload as RelationStep2;
      const filteredOffering = state.relationsStep2.filter(
        offer => offer.partnerOfferingId !== offerings.partnerOfferingId,
      );
      return { ...state, relationsStep2: [...filteredOffering, { ...offerings }] };
    }
    case REMOVE_RELATION_STEP2:
      return {
        ...state,
        relationsStep2: state.relationsStep2.filter(
          item => item.partnerOfferingId !== action.payload,
        ),
      };
    case RESET_RELATION_STEP2:
      return { ...state, relationsStep2: action.payload as RelationStep2[] };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosCall = services.axios.Axios();

  const selectPractice = (practice: Practice) => ({ type: SELECT_PRACTICE, payload: practice });

  const unSelectPractice = (practice: Practice | undefined, tabId: number) => ({
    type: UNSELECT_PRACTICE,
    payload: { practice, tabId },
  });

  const setCurrentPracticeId = (id: string) => ({ type: SET_PRACTICE_ID, payload: id });

  const setCurrentPractice = (practice: Practice = {}) => ({
    type: GET_PRACTICE,
    payload: practice,
  });

  const setDashboardStats = (stats: Record<string, unknown>) => ({
    type: SET_DASHBOARD_STATS,
    payload: stats,
  });

  const setPracticeNotifications = (notifications: number) => ({
    type: SET_PRACTICE_NOTIFICATIONS,
    payload: notifications,
  });

  const setLatestRuntimeJobExtraction = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_EXTRACTION,
    payload: timeData,
  });

  const setLatestRuntimeJobAttachmentSync = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_ATTACHMENT_SYNC,
    payload: timeData,
  });

  const setLatestRuntimeJobStaging = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_STAGING,
    payload: timeData,
  });

  const setLatestRuntimeJobIntermediate = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_INTERMEDIATE,
    payload: timeData,
  });

  const setLatestRuntimeInstanceReport = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_INSTANCE_REPORT,
    payload: timeData,
  });

  const setLatestRuntimeConversionPrep = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_CONVERSIONPREP,
    payload: timeData,
  });

  const setLatestRuntimeLeadsGeneration = (timeData: unknown) => ({
    type: GET_LATEST_RUNTIME_LEADS_GENERATION,
    payload: timeData,
  });

  const practiceError = (error: string | null) => ({ type: PRACTICE_ERROR, payload: error });

  const closePracticeTab = (practice: Practice | undefined, tabId: number) => {
    return async (dispatch: Dispatch) => {
      dispatch(unSelectPractice(practice, tabId));
      dispatch(
        actionsStore.toggle.toggleEditingPractice(false) as unknown as ReturnType<
          typeof unSelectPractice
        >,
      );
    };
  };

  const fetchPractice = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get(`practices/${practiceId}`);
        const data = (response as { data: { data: Practice } })?.data?.data;
        if (data) {
          dispatch(setCurrentPracticeId(data?.id || ''));
          dispatch(setCurrentPractice(data));
          dispatch(
            actionsStore.toggle.toggleIsEditingPractice(false) as unknown as ReturnType<
              typeof setCurrentPractice
            >,
          );
          dispatch(selectPractice(data));
          return data;
        }
        return '';
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const setPracticeShardsLoading = (practiceId: string) => ({
    type: SET_PRACTICE_SHARDS_LOADING,
    payload: practiceId,
  });

  const setPracticeShards = (practiceId: string, shards: PracticeShard[]) => ({
    type: SET_PRACTICE_SHARDS,
    payload: { practiceId, shards },
  });

  const setPracticeShardsUnavailable = (practiceId: string) => ({
    type: SET_PRACTICE_SHARDS_UNAVAILABLE,
    payload: practiceId,
  });

  const setPracticeShardsForbidden = (practiceId: string) => ({
    type: SET_PRACTICE_SHARDS_FORBIDDEN,
    payload: practiceId,
  });

  /**
   * Fetches the practice's shard assignments. Only called for users holding
   * ViewShardInformation — the endpoint returns 403 otherwise.
   */
  const getPracticeShards = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      dispatch(setPracticeShardsLoading(practiceId));
      try {
        const response = await axiosCall.get(`practices/${practiceId}/shards`, {
          timeout: SHARD_REQUEST_TIMEOUT_MS,
        });
        const data = (response as { data: { data: PracticeShardsResponse } })?.data?.data;
        if (!data || data.isUnavailable) {
          return dispatch(setPracticeShardsUnavailable(practiceId));
        }
        return dispatch(setPracticeShards(practiceId, data.shards ?? []));
      } catch (e) {
        // Swallowed on purpose. A shard-service outage must not raise a page-level error or a
        // toast — the block renders a single "unavailable" line and the rest of the practice page
        // is unaffected.
        //
        // A 403 is kept distinct: it means the API rejected the caller, not that the shard service
        // is down, so reporting it as an outage would be a fabricated fact. The UI hides the block
        // instead. This is reachable in production when a permission is revoked mid-session (the
        // token still carries it) and locally when the development override
        // (REACT_APP_DEV_PERMISSIONS) names one the account does not actually hold.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          return dispatch(setPracticeShardsForbidden(practiceId));
        }
        return dispatch(setPracticeShardsUnavailable(practiceId));
      }
    };
  };

  const createPractice = (payload: unknown) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.post('practices', payload);
        return (response as { data: { data: Practice } }).data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
        toast.error('Error: Something occurred during create practice process!', {
          duration: 7500,
          style: { minWidth: '25vw' },
        });
      }
    };
  };

  const saveEditedPracticeInfo = (editedPracticeInfo: { id: string; [key: string]: unknown }) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.patch(
          `practices/${editedPracticeInfo.id}`,
          editedPracticeInfo,
        );
        return (response as unknown as { data: { data: Practice } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const addPracticeBookmark = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/bookmark`);
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const removePracticeBookmark = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.delete(`practices/${practiceId}/bookmark`);
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  let getPracticesCancel = new AbortController();
  const getPractices = ({
    practiceTab = 'all',
    filter = '',
    onlyShowBookmarked = false,
    onlyShowAssigned = false,
    assignedFilterUser = null,
    includeTestPractices = false,
    includeDormantPractices = false,
    searchPracticeNotes = false,
    searchPracticeGroups = false,
    searchCustomIds = false,
    searchPartnerOfferings = false,
    searchPracticePims = false,
    skip = null,
  }) => {
    return async (dispatch: Dispatch) => {
      try {
        getPracticesCancel.abort();
        getPracticesCancel = new AbortController();
        const params: Record<string, unknown> = {
          skip,
          pageSize: 100,
        };
        if (filter !== '') params.filter = filter;
        if (onlyShowBookmarked === true) params.onlyShowBookmarked = true;
        if (practiceTab.toLowerCase() === 'stale') params.onlyShowStale = true;
        if (practiceTab.toLowerCase() === 'manual_conversions')
          params.onlyShowManualConversions = true;
        if (practiceTab.toLowerCase() === 'conversions') params.onlyShowConversions = true;
        if (practiceTab.toLowerCase() === 'live') params.onlyShowLive = true;
        if (practiceTab.toLowerCase() === 'new') params.onlyShowNew = true;
        if (practiceTab.toLowerCase() === 'muted') params.onlyShowMuted = true;
        if (practiceTab.toLowerCase() === 'dormant') params.onlyShowDormant = true;
        if (onlyShowAssigned === true) params.onlyShowAssigned = true;
        if (assignedFilterUser !== null) params.assignedAccountId = assignedFilterUser;
        if (includeTestPractices === true) params.includeTestPractices = true;
        if (includeDormantPractices === true) params.includeDormantPractices = true;
        if (searchPracticeNotes === true) params.searchNotes = true;
        if (searchPracticeGroups === true) params.searchPracticeGroups = true;
        if (searchCustomIds === true) params.searchCustomIds = true;
        if (searchPartnerOfferings === true) params.searchPartnerOfferings = true;
        if (searchPracticePims === true) params.searchPracticePims = true;

        const query = await axiosCall.get('v2/practices', {
          params,
          signal: getPracticesCancel.signal,
        });
        dispatch({ type: SEARCH_PRACTICES_RESULT, payload: query });
        return query;
      } catch (error: unknown) {
        if ((error as Error).name === 'CanceledError') return;
        dispatch({ type: SEARCH_PRACTICE_ERROR, payload: (error as Error).message });
      }
    };
  };

  const getAllPracticeGroups = () => {
    return async (dispatch: Dispatch) => {
      const groups = await axiosCall.get('practices/groups');
      dispatch({ type: SET_PRACTICE_GROUPS, payload: groups?.data || [] });
    };
  };

  const setAssignedUser = (practiceId: string, accountId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/assign`, { id: accountId });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const removeAssignedUser = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.delete(`practices/${practiceId}/assign`);
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const getPracticeConfiguration = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get(`practices/${practiceId}/configuration`);
        return (response as { data: { data: unknown } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const createPracticeInstaller = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.post(`practices/${practiceId}/installer`);
        return (response as { data: { data: unknown } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const savePracticeConfiguration = (practiceId: string, config: unknown) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.patch(`practices/${practiceId}/configuration`, config);
        return (response as { data: { success: boolean } }).data.success ? true : false;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const getAttachmentSyncWindow = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get(`practices/${practiceId}/attachmentsyncwindow`);
        return (response as { data: { data: unknown } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  // unlike savePracticeConfiguration, this rejects on failure instead of swallowing the
  // error - the attachment window form needs to know a save failed so it can keep the
  // modal open and show the error, rather than closing as if it had succeeded
  const saveAttachmentSyncWindow = (practiceId: string, window: unknown) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.patch(
          `practices/${practiceId}/attachmentsyncwindow`,
          window,
        );
        if (!(response as { data: { success: boolean } }).data.success) {
          throw new Error('Save failed.');
        }
      } catch (error: unknown) {
        const message = (error as Error).message || 'Save failed.';
        dispatch(practiceError(message));
        throw new Error(message);
      }
    };
  };

  const mutePractice = (practiceId: string, payload: unknown) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.post(`practices/${practiceId}/mute`, {
          ...(payload as object),
        });
        return (response as unknown as { data: { data: unknown } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const unmutePractice = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.delete(`practices/${practiceId}/mute`);
        return (response as { data: { data: unknown } }).data.data;
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerAgentRestart = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/restart`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeAttachmentSync = (
    message: string,
    practiceId: string,
    sourceFolder: string,
    isRecursive: boolean,
    includedFiles: string,
    excludedFiles: string,
    snapshotMode: boolean,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/attachmentsync`, {
          message,
          sourceFolder,
          isRecursive,
          includedFiles,
          excludedFiles,
          snapshotMode,
        });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeAttachmentSyncFromIntermediate = (
    message: string,
    practiceId: string,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/attachmentsyncbatchfromintermediate`, {
          message,
        });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const terminateAttachmentSync = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/terminateattachmentsync`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeExtraction = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/extract`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const terminateDataExtraction = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/terminateextraction`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeStagingLoad = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/staging`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeIntermediateLoad = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/intermediate`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeDaylightSync = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/daylightsync`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeReportingSync = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/reportingsync`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const getLatestInstanceReportRuntime = (instanceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get(
          `ReportDelivery/InstanceReport?instanceId=${instanceId}`,
        );
        const data = (response as { data: { instanceReportRan?: string[] } }).data;
        if (data?.instanceReportRan?.length) {
          return dispatch(setLatestRuntimeInstanceReport(data));
        }
        return dispatch(
          setLatestRuntimeInstanceReport({ instanceId, instanceReportRan: 'Runtime not found' }),
        );
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeLeadsGeneration = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/leadsgeneration`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const triggerManualPracticeAttachmentPrep = (practiceId: string, message = '') => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`practices/${practiceId}/attachmentprep`, { message });
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const getLatestRuntime = (practiceId: string, jobType = '') => {
    return async (dispatch: Dispatch) => {
      try {
        const params = { jobType };
        const response = await axiosCall.get(`practices/${practiceId}/latestruntimebyjobtype`, {
          params,
        });
        const data = (response as { data: { data: Record<string, unknown> } }).data.data;
        switch (jobType) {
          case 'Extraction':
          case 'ExtractionInitiate':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeJobExtraction(data));
            }
            return dispatch(setLatestRuntimeJobExtraction('Runtime not found'));
          case 'AttachmentSync':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeJobAttachmentSync(data));
            }
            return dispatch(setLatestRuntimeJobAttachmentSync('Runtime not found'));
          case 'StagingLoad':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeJobStaging(data));
            }
            return dispatch(setLatestRuntimeJobStaging('Runtime not found'));
          case 'IntermediateLoad':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeJobIntermediate(data));
            }
            return dispatch(setLatestRuntimeJobIntermediate('Runtime not found'));
          case 'ConversionPrep':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeConversionPrep(data));
            }
            return dispatch(setLatestRuntimeConversionPrep('Runtime not found'));
          case 'LeadsGeneration':
            if (Object.keys(data).length !== 0) {
              return dispatch(setLatestRuntimeLeadsGeneration(data));
            }
            return dispatch(setLatestRuntimeLeadsGeneration('Runtime not found'));
          default:
            return 'Runtime not found';
        }
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const getDashboardStats = () => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get(`practices/dashboard`);
        const data = (
          response as { data: { data: { notificationCount: number; [key: string]: unknown } } }
        ).data.data;
        dispatch(setDashboardStats(data));
        dispatch(setPracticeNotifications(data.notificationCount));
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const clearHardwareId = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.delete(`practices/${practiceId}/instances/clear`);
      } catch (error: unknown) {
        dispatch(practiceError((error as Error).message));
      }
    };
  };

  const addOfferingStep2 = (offering: RelationStep2) => ({
    type: SET_RELATION_STEP2,
    payload: offering,
  });

  const removeOfferingsStep2 = (partnerOfferingId: string) => ({
    type: REMOVE_RELATION_STEP2,
    payload: partnerOfferingId,
  });

  const resetOfferingsStep2 = (relations: RelationStep2[] = []) => ({
    type: RESET_RELATION_STEP2,
    payload: relations,
  });

  const editPractice = (payload: { id: string; [key: string]: unknown }) => async () =>
    axiosCall.patch('practices/' + payload.id, payload);

  const getAssignedUser = (user: string | null) => ({ type: SET_ASSIGNED_USER, payload: user });

  const getPractice =
    (id: string, getForWorkflow = false) =>
    async () => {
      const params: Record<string, unknown> = {};
      if (getForWorkflow) params.getForWorkflow = true;
      return axiosCall.get('practices/' + id, { params });
    };

  const selectPracticeToAssignToUser = (practice: UserAssigningPractice) => ({
    type: USER_ASSIGNING_PRACTICE,
    payload: practice,
  });

  const selectPracticeToMute = (practice: PracticeToMute) => ({
    type: PRACTICE_TO_MUTE,
    payload: practice,
  });

  const setActiveTabPractices = (tab: string) => ({ type: SET_ACTIVE_TAB_PRACTICES, payload: tab });

  const setBookmarked = (bool: boolean) => ({ type: SET_BOOKMARKED, payload: bool });

  const setCurrentPage = (currentPage: number) => ({
    type: SET_CURRENT_PAGE,
    payload: currentPage,
  });

  const setFilter = (filter: string) => ({ type: SET_FILTER, payload: filter });

  const setIncludeDormantPractices = (value = false) => ({
    type: SET_INCLUDE_DORMANT_PRACTICES,
    payload: value,
  });

  const setIncludeTestPractices = (value = false) => ({
    type: SET_INCLUDE_TEST_PRACTICES,
    payload: value,
  });

  const setOnlyShowAssigned = (bool: boolean) => ({
    type: SET_SEARCH_ASSIGNED_PRACTICES,
    payload: bool,
  });

  const setPracticeConfigModalWorkingPracticeId = (practiceId = '') => ({
    type: SET_CONFIG_MODAL_WORKING_PRACTICE,
    payload: practiceId,
  });

  const setPracticeRelationsModalWorkingPractice = (practice: Record<string, unknown> = {}) => ({
    type: SET_RELATIONS_MODAL_WORKING_PRACTICE,
    payload: practice,
  });

  const setPracticeScheduleModalWorkingPractice = (
    practice: ScheduleModalPractice = { nextSyncDate: '', id: '' },
  ) => ({ type: SET_SCHEDULE_MODAL_WORKING_PRACTICE, payload: practice });

  const setSearchCustomIds = (bool: boolean) => ({ type: SET_SEARCH_CUSTOM_IDS, payload: bool });

  const setSearchGroups = (bool: boolean) => ({ type: SET_SEARCH_GROUPS, payload: bool });

  const setSearchNotes = (bool: boolean) => ({ type: SET_SEARCH_NOTES, payload: bool });

  const setSearchPartnerOfferings = (bool: boolean) => ({
    type: SET_SEARCH_PARTNER_OFFERINGS,
    payload: bool,
  });

  const setSearchPracticePims = (bool: boolean) => ({
    type: SET_SEARCH_PRACTICE_PIMS,
    payload: bool,
  });

  return {
    addOfferingStep2,
    addPracticeBookmark,
    clearHardwareId,
    closePracticeTab,
    createPractice,
    createPracticeInstaller,
    editPractice,
    fetchPractice,
    getAllPracticeGroups,
    getAssignedUser,
    getAttachmentSyncWindow,
    getDashboardStats,
    getLatestInstanceReportRuntime,
    getLatestRuntime,
    getPractice,
    getPracticeConfiguration,
    getPracticeShards,
    getPractices,
    mutePractice,
    practiceError,
    removeAssignedUser,
    removeOfferingsStep2,
    removePracticeBookmark,
    resetOfferingsStep2,
    saveAttachmentSyncWindow,
    saveEditedPracticeInfo,
    savePracticeConfiguration,
    selectPracticeToAssignToUser,
    selectPracticeToMute,
    setActiveTabPractices,
    setAssignedUser,
    setBookmarked,
    setCurrentPage,
    setFilter,
    setIncludeDormantPractices,
    setIncludeTestPractices,
    setOnlyShowAssigned,
    setPracticeConfigModalWorkingPracticeId,
    setPracticeRelationsModalWorkingPractice,
    setPracticeScheduleModalWorkingPractice,
    setSearchCustomIds,
    setSearchGroups,
    setSearchNotes,
    setSearchPartnerOfferings,
    setSearchPracticePims,
    terminateAttachmentSync,
    terminateDataExtraction,
    triggerAgentRestart,
    triggerManualPracticeAttachmentSync,
    triggerManualPracticeAttachmentSyncFromIntermediate,
    triggerManualPracticeDaylightSync,
    triggerManualPracticeExtraction,
    triggerManualPracticeIntermediateLoad,
    triggerManualPracticeLeadsGeneration,
    triggerManualPracticeAttachmentPrep,
    triggerManualPracticeReportingSync,
    triggerManualPracticeStagingLoad,
    unmutePractice,
  };
};
